import './sidebar-account-card.css';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretUp, SignOut, Sun, Moon, UserCircle, Users, CreditCard, Vault, GearSix } from '@phosphor-icons/react';
import { useCurrency } from '@/hooks/useCurrency';
import { ThemeToggleCompact } from './ThemeToggle';
import { CurrencySelector } from './CurrencySelector';
import FounderboardCoinIcon from './FounderboardCoinIcon';

/**
 * Consolidated bottom-of-sidebar control: avatar + name + small currency/theme
 * indicator icons. Clicking it pops a floating panel out above the trigger
 * (absolutely positioned, not laid out in the sidebar's own flow) with a
 * staggered entrance per row. Sub-items: Profile, Vault, Billing, Currency,
 * Theme, Logout.
 *
 * `coins` — Founderboard Coins balance (startups only). A number shows the coin
 * on the card and a Coins row in the menu; null/undefined hides both.
 */
export const SidebarProfileMenu = ({ name, avatarUrl, expanded = false, onLogout, basePath = '', coins = null }) => {
  const showCoins = typeof coins === 'number';
  const coinsLabel = showCoins ? coins.toLocaleString('en-IN') : '';
  const navigate = useNavigate();
  const { currency, symbol } = useCurrency();
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState(() => document.documentElement.getAttribute('data-theme') || 'light');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    const target = document.documentElement;
    const observer = new MutationObserver(() => {
      setTheme(target.getAttribute('data-theme') || 'light');
    });
    observer.observe(target, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const goTo = (path) => {
    setOpen(false);
    navigate(`${basePath}${path}`);
  };

  const nameParts = (name || '').trim().split(/\s+/).filter(Boolean);
  const firstName = nameParts[0] || 'Account';
  const initials = nameParts.length >= 2
    ? (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase()
    : firstName.substring(0, 2).toUpperCase();

  const rowCls = 'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md transition-all duration-150 text-left';

  return (
    <div className="relative" ref={ref}>
      {open && expanded && (
        <div
          className="absolute bottom-[calc(100%+8px)] left-0 right-0 z-[80] rounded-xl border p-1.5 shadow-[0_14px_35px_rgba(15,23,42,0.14)]" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
          data-testid="sidebar-profile-menu"
        >
          <div className="px-2.5 pb-2 pt-1.5">
            <div className="text-[9px] font-bold uppercase tracking-[0.14em]" style={{ color: 'var(--muted-foreground)' }}>Account</div>
            <div className="mt-0.5 truncate text-[12px] font-semibold" style={{ color: 'var(--foreground)' }} title={name}>{name || 'Account'}</div>
          </div>

          <div className="space-y-0.5">
            <button onClick={() => goTo('/profile')} className={rowCls} style={{ color: 'var(--muted-foreground)' }} data-testid="sidebar-profile-link">
              <UserCircle size={16} weight="bold" className="shrink-0" />
              <span className="text-[12px] font-semibold">Profile</span>
            </button>
            <button onClick={() => goTo('/team')} className={rowCls} style={{ color: 'var(--muted-foreground)' }} data-testid="sidebar-team-link">
              <Users size={16} weight="bold" className="shrink-0" />
              <span className="text-[12px] font-semibold">Team</span>
            </button>
            {basePath === '/startup' && (
              <button onClick={() => goTo('/vault')} className={rowCls} style={{ color: 'var(--muted-foreground)' }} data-testid="sidebar-vault-link">
                <Vault size={16} weight="bold" className="shrink-0" />
                <span className="text-[12px] font-semibold">Vault</span>
              </button>
            )}
            <button onClick={() => goTo('/billing')} className={rowCls} style={{ color: 'var(--muted-foreground)' }} data-testid="sidebar-billing-link">
              <CreditCard size={16} weight="bold" className="shrink-0" />
              <span className="text-[12px] font-semibold">Billing</span>
            </button>
            {showCoins && (
              <button onClick={() => goTo('/billing#coins')} className={rowCls} style={{ color: 'var(--muted-foreground)' }} data-testid="sidebar-coins-link">
                <FounderboardCoinIcon size={16} />
                <span className="text-[12px] font-semibold flex-1">FB Coins</span>
                <span className="font-mono text-[11px] font-bold tabular-nums text-amber-600 dark:text-amber-400">{coinsLabel}</span>
              </button>
            )}
          </div>

          <div className="my-1.5 border-t" style={{ borderColor: 'var(--border)' }} />

          <div className="rounded-lg p-2" style={{ backgroundColor: 'var(--background)' }}>
            <div className="mb-1.5 flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.12em]" style={{ color: 'var(--muted-foreground)' }}>
              <GearSix size={12} weight="bold" /> Preferences
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="w-14 text-[10px] font-medium" style={{ color: 'var(--muted-foreground)' }}>Currency</span>
                <div className="min-w-0 flex-1"><CurrencySelector expanded /></div>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-14 text-[10px] font-medium" style={{ color: 'var(--muted-foreground)' }}>Theme</span>
                <div className="min-w-0 flex-1"><ThemeToggleCompact expanded /></div>
              </div>
            </div>
          </div>

          <div className="mt-1.5 border-t pt-1.5" style={{ borderColor: 'var(--border)' }}>
            <button
              onClick={() => { setOpen(false); onLogout?.(); }}
              className="w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left font-semibold transition-all duration-150"
              style={{ color: '#dc2626', backgroundColor: 'rgba(220,38,38,0.055)' }}
              onMouseEnter={(event) => { event.currentTarget.style.backgroundColor = 'rgba(220,38,38,0.12)'; event.currentTarget.style.color = '#ef4444'; }}
              onMouseLeave={(event) => { event.currentTarget.style.backgroundColor = 'rgba(220,38,38,0.055)'; event.currentTarget.style.color = '#dc2626'; }}
              data-testid="logout-button"
            >
              <SignOut size={16} weight="bold" className="shrink-0" />
              <span className="text-[12px] font-semibold">Sign out</span>
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        title={expanded ? undefined : name}
        aria-expanded={open}
        className={`group w-full flex items-center gap-2.5 rounded-lg transition-all duration-150 ${expanded ? 'px-2 py-2' : 'p-1.5 justify-center'}`} style={{ color: 'var(--foreground)' }}
        data-testid="sidebar-profile-menu-btn"
      >
        <span className="relative shrink-0">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="h-8 w-8 rounded-lg object-cover ring-1 ring-border" />
          ) : (
            <div className="h-8 w-8 rounded-lg border flex items-center justify-center" style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border)' }}>
              <span className="font-mono text-[10px] font-bold" style={{ color: 'var(--muted-foreground)' }}>{initials}</span>
            </div>
          )}
          {/* Collapsed rail has no room for the pill — a coin on the avatar corner stands in. */}
          {showCoins && !expanded && (
            <FounderboardCoinIcon size={14} className="absolute -bottom-1 -right-1" title={`${coinsLabel} FB Coins`} />
          )}
        </span>

        {expanded && (
          <>
            <div className="min-w-0 flex-1 text-left">
              <div className="truncate text-[12px] font-semibold" style={{ color: 'var(--foreground)' }}>{firstName}</div>
              <div className="mt-0.5 flex items-center gap-1.5 text-[9px]" style={{ color: 'var(--muted-foreground)' }}>
                <span>{symbol || currency}</span>
                <span className="h-0.5 w-0.5 rounded-full opacity-60" style={{ backgroundColor: 'var(--muted-foreground)' }} />
                <span className="inline-flex items-center gap-1">
                  {theme === 'dark' ? <Moon size={10} weight="fill" /> : <Sun size={10} weight="fill" />}
                  {theme === 'dark' ? 'Dark' : 'Light'}
                </span>
                {showCoins && (
                  <>
                    <span className="h-0.5 w-0.5 rounded-full opacity-60" style={{ backgroundColor: 'var(--muted-foreground)' }} />
                    <span className="inline-flex items-center gap-1 font-semibold" title={`${coinsLabel} Founderboard Coins`} data-testid="sidebar-coins-info">
                      <FounderboardCoinIcon size={11} />
                      <span className="tabular-nums" style={{ color: 'var(--foreground)' }}>{coinsLabel}</span>
                    </span>
                  </>
                )}
              </div>
            </div>
            <CaretUp
              size={14}
              weight="bold"
              className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} style={{ color: 'var(--muted-foreground)' }}
            />
          </>
        )}
      </button>
    </div>
  );
};

export default SidebarProfileMenu;
